<template>
    <div>
        <div v-if="init">
            <div class="header-actions">
                <CorpId />
                <CompassButton
                    :label="$t.__('developerPage.ApiV2.create_api_access')"
                    :disabled="clients.length >= maxTokens"
                    class="ml-3"
                    @click="showCreateClientForm()"
                />
            </div>

            <el-table
                :data="formatedClients"
                :defaultSort="{ prop: 'name', order: 'ascending' }"
                border
            >
                <el-table-column :label="$t.__('common.labels.name')" prop="name" sortable />
                <el-table-column :label="$t.__('common.labels.type')" prop="type">
                    <template #default="scope">
                        <span v-if="scope.row.type === 'personal_access_token'">{{
                            $l10n.__('Token personnel')
                        }}</span>
                        <span v-else-if="scope.row.type === 'personal'">{{
                            $l10n.__('Personnel')
                        }}</span>
                        <span v-else-if="scope.row.type === 'private'">{{
                            $l10n.__('Privé')
                        }}</span>
                        <span v-else-if="scope.row.type === 'public'">{{
                            $l10n.__('Publique')
                        }}</span>
                        <span v-if="scope.row.type === 'public' && !scope.row.validated_at"
                            >({{ $l10n.__('En attente de validation') }})</span
                        >
                        <span v-if="scope.row.type === 'personal' && scope.row.delegated"
                            >({{ $l10n.__('Accès délégué') }})</span
                        >
                    </template>
                </el-table-column>
                <el-table-column :label="$t.__('developerPage.ApiV2.scopes')" prop="scopes">
                    <template #default="scope">
                        <CompassTag
                            v-for="item in scope.row.scopes"
                            :key="item"
                            color="stroked"
                            class="api-scope"
                        >
                            {{ item }}
                        </CompassTag>
                    </template>
                </el-table-column>
                <el-table-column :label="$t.__('common.labels.staff')" prop="user" sortable>
                    <template #default="scope">
                        <person-list
                            v-if="scope.row.user && scope.row.user.staffid"
                            :list="[scope.row.user]"
                        />
                        <span v-else-if="scope.row.user && scope.row.user.type === 'staff'"
                            >{{ $l10n.__('Collaborateur') }} {{ scope.row.user.id }}</span
                        >
                        <span v-else-if="scope.row.user"
                            >{{ $l10n.__('Contact') }} {{ scope.row.user.id }}</span
                        >
                    </template>
                </el-table-column>

                <!-- CONTEXT MENU -->
                <el-table-column
                    fixed="right"
                    label=""
                    width="40"
                    className="el-table-column--contextmenu"
                >
                    <template #default="scope">
                        <CompassMenu
                            trigger="click"
                            customClass="api-context-menu"
                            placement="left-start"
                        >
                            <CompassIcon role="button" type="link" size="medium" icon="action" />
                            <template #dropdown>
                                <CompassDropdownOption
                                    :key="`contextmenu${scope.row.id}_edit`"
                                    :disabled="scope.row.type === 'personal_access_token'"
                                    :label="$t.__('developerPage.ApiV2.edit_token')"
                                    @itemSelected="editClient(scope.row)"
                                />
                                <CompassDropdownOption
                                    :key="`contextmenu${scope.row.id}_removed`"
                                    :label="$t.__('developerPage.ApiV2.delete_token')"
                                    @itemSelected="removeClient(scope.row.id, scope.row.name)"
                                />
                            </template>
                        </CompassMenu>
                    </template>
                </el-table-column>
            </el-table>

            <api-client-form
                v-if="showForm"
                :isVisible="showForm"
                :staffs="availableStaffs"
                :scopes="scopes"
                :scopesTree="scopesTree"
                :editedClient="editedClient"
                @close-form="showForm = false"
                @create="clientCreated"
                @update="updateClient"
            />
        </div>
        <preloader v-if="loading" :background="false" :display="true" />
    </div>
</template>

<script>
import ApiClientForm from './ApiClientForm.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import PersonList from 'vcomponents/utilities/PersonList.vue';
import ContextMenuMixins from 'mixins/ContextMenuMixins';
import CorpId from 'vcomponents/developerPage/CorpId.vue';

export default {
    components: {
        Preloader,
        CorpId,
        'person-list': PersonList,
        'api-client-form': ApiClientForm,
    },
    mixins: [ContextMenuMixins],
    data() {
        return {
            showForm: false,
            editedClient: null,
            clients: [],
            init: false,
            loading: true,
            staffs: {},
            profiles: {},
            scopes: [],
            scopesTree: {},
            maxTokens: 0,
        };
    },
    computed: {
        availableStaffs() {
            return Object.values(this.staffs).filter((staff) => staff.status === 'ok');
        },
        formatedClients() {
            return this.clients.map((token) => {
                const scopes = [];

                // Format scopes
                if (token.scopes.indexOf('all') !== -1) {
                    scopes.push('all');
                    if (token.scopes.indexOf('private') !== -1) {
                        scopes.push('private');
                    }
                } else {
                    token.scopes.forEach((scope) => scopes.push(scope));
                }

                token.scopes = scopes.filter(function (scope) {
                    const scopeParts = scope.split('.', 2);
                    return scopeParts.length === 2
                        ? token.scopes.indexOf(scopeParts[0]) === -1
                        : true;
                });

                //Format user
                if (
                    token.user &&
                    token.user.type === 'staff' &&
                    this.staffs[token.user.id] !== undefined
                ) {
                    token.user = this.staffs[token.user.id];
                }

                return token;
            });
        },
    },
    async created() {
        try {
            await this.fetchProfiles();

            let response = await ApiClient.get(`/personal-access-tokens/metas`);
            response.data.staffs.map((staff) => {
                this.staffs[staff.id] = {
                    id: staff.id,
                    staffid: staff.id,
                    type: 'staff',
                    fullName: staff.lastname + ' ' + staff.firstname,
                    firstname: staff.firstname,
                    lastname: staff.lastname,
                    email: staff.email,
                    status: staff.status,
                    avatar: staff.avatar !== null ? staff.avatar : {},
                    isAdmin: this.profiles[staff.profile]
                        ? this.profiles[staff.profile].is_administrator
                        : false,
                };
            });

            response = await ApiClient.get(`/scopes`);
            this.scopes = response.data;

            response = await ApiClient.get(`/scopes/tree`);
            this.scopesTree = response.data;

            response = await ApiClient.get('/clients');
            this.clients = response.data.data;
            this.maxTokens = response.data.pagination.limit;
        } catch (error) {
            Push.error(this.$l10n.__('Problème lors de la récupération des données'));
        }

        this.loading = false;
        this.init = true;
    },
    methods: {
        async fetchProfiles(offset) {
            try {
                const res = await ApiClient.get(`/profiles`, {
                    limit: 100,
                    ...offset,
                });

                res.data.data.forEach((profile) => {
                    this.profiles[profile.id] = profile;
                });

                if (Object.keys(this.profiles).length < res.data.pagination.total) {
                    await this.fetchProfiles({
                        offset: Object.keys(this.profiles).length,
                    });
                }
            } catch (error) {
                Push.error(this.$l10n.__('Problème lors de la récupération des données'));
            }
        },
        removeClient(clientId, name) {
            this.$confirm(
                this.$l10n.__(
                    "La suppression d'un accès API est définitive. Si une application l'utilise, elle n'aura plus accès à l'API.",
                ) +
                    ' ' +
                    this.$l10n.__('Êtes-vous sûr de vouloir continuer ?'),
                { title: this.$l10n.__('Suppression du l\'accès "%s"', name) },
            )
                .then(() => {
                    this.loading = true;
                    ApiClient.del('/clients/' + clientId)
                        .then(() => {
                            Push.success(this.$l10n.__('Accès "%s" supprimé', name), undefined, {
                                escapeHtml: true,
                            });
                            //Live remove token
                            this.clients = this.clients.filter((client) => client.id !== clientId);
                        })
                        .catch(() =>
                            Push.error(this.$l10n.__('Problème lors de la suppression du client')),
                        );
                })
                .catch(() => {})
                .finally(() => (this.loading = false));
        },
        editClient(client) {
            this.editedClient = client;
            this.showForm = true;
        },
        showCreateClientForm() {
            this.editedClient = null;
            this.showForm = true;
        },
        clientCreated(client) {
            this.clients.push(client);
        },
        updateClient(client) {
            for (const key in this.clients) {
                if (this.clients[key].id !== client.id) {
                    continue;
                }
                this.clients[key] = client;
            }
            Push.success(this.$l10n.__("L'accès api a été mis à jour avec succès"));
        },
    },
};
</script>

<style>
#compass-menu-container .api-context-menu {
    width: 200px !important;
    z-index: 4 !important;
}
.el-table-column--contextmenu .compass-menu {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}
.el-table-column--contextmenu .compass-menu .compass-icon {
    cursor: pointer;
}
</style>
<style lang="scss" scoped>
.api-scope {
    & + .api-scope {
        margin-left: var(--spacing-0);
    }
}
</style>
